/**
 * Core game types. Deliberately plain — no schema library in here, because the engine
 * has to stay dependency-free. Wire validation lives in `shared/`, which parses into
 * these types at the boundary.
 */

export type PlayerIndex = 0 | 1;

export type PieceType = 'king' | 'pawn';

export interface Piece {
  readonly player: PlayerIndex;
  readonly type: PieceType;
}

/**
 * Starting arrangements. The 2023 build also had a `'tutorial'` formation — an empty
 * board that the server let a privileged bot mutate mid-game. The tutorial is now a
 * client-side script running against a local engine, so that special case is gone and
 * the server no longer has a god-mode code path.
 */
export type FormationName = 'monarchy' | 'diarchy' | 'triarchy' | 'colonies';

export const FORMATION_NAMES: readonly FormationName[] = [
  'monarchy',
  'diarchy',
  'triarchy',
  'colonies',
];

export interface Variant {
  readonly radius: number;
  readonly formation: FormationName;
  /** Pawns each player may place over the course of the game. */
  readonly spawnsAvailable: number;
  readonly timeInitialMs: number;
  readonly timeIncrementMs: number;
  /** Rating multiplier; `0` means casual/unrated. */
  readonly stakes: number;
}

/**
 * A move. `fromIndex`/`toIndex` are cell indices into the board of the match's variant.
 *
 * Names match the 2023 build on purpose: Phase 1 ports the old rules verbatim and
 * compares against them, and identical field names keep that diff honest.
 */
export type Move =
  | { readonly type: 'movePiece'; readonly fromIndex: number; readonly toIndex: number }
  | { readonly type: 'spawnPiece'; readonly fromIndex: number; readonly toIndex: number }
  | { readonly type: 'pass' }
  | { readonly type: 'resign' };

export type MoveType = Move['type'];

/**
 * How a match ended.
 *
 * `resignation` is new: the 2023 build had `ResignMove` in its move union but no reducer
 * case for it and no generator that emitted it, and its status enum had no matching
 * member — so resigning was rejected by the server and would have crashed the client if
 * it ever got through.
 *
 * `repetition` is also new — threefold repetition. Note it is a property of the game
 * *history*, not of a position, so it lives with the match rather than in movegen, and
 * it does not affect perft.
 */
export type MatchStatus =
  | 'playing'
  | 'aborted'
  | 'drawn'
  | 'repetition'
  | 'checkmate'
  | 'timeout'
  | 'resignation';

export const TERMINAL_STATUSES: readonly MatchStatus[] = [
  'aborted',
  'drawn',
  'repetition',
  'checkmate',
  'timeout',
  'resignation',
];

export const isTerminal = (status: MatchStatus): boolean => status !== 'playing';

/**
 * Everything movegen needs, and nothing else. Clocks, chat, player identity and the move
 * log live on the match, not here — which is what lets the bot search millions of these
 * without dragging match bookkeeping along.
 */
export interface Position {
  readonly variant: Variant;
  readonly playerToMove: PlayerIndex;
  /** One entry per cell, `null` when empty. Indexed by board cell index. */
  readonly cells: readonly (Piece | null)[];
  /** Remaining spawns, per player index. */
  readonly spawnsAvailable: readonly [number, number];
}
